import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronRight, Info, Search, X } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
import AdSlot from '../components/advertisement/AdSlot';
import NewsDeskList from '../components/news-desk/NewsDeskList';
import NewsDeskSidebar from '../components/news-desk/NewsDeskSidebar';
import { NEWS_DESK_TABS, NEWS_SORTS, buildNewsDeskRows, countNewsTabs, filterNewsDesk, newsTabByKey } from '../utils/newsDesk';
import { formatRefreshedAt } from '../utils/gmpBoard';
import { fetchNews, selectNewsArticles } from '../store/slices/newsSlice';
import { fetchGmp, selectGmpBoardRows } from '../store/slices/gmpSlice';
import { fetchSubscriptionSummary, selectSubscriptionRows } from '../store/slices/subscriptionSlice';

const PAGE_SIZE = 20;
const NOTICE_MS = 4500;
const NEWS_MAX_AGE_MS = 5 * 60000;
const DATA_MAX_AGE_MS = 60000;
const PAGE_TITLE = 'IPO News — Listings, Allotments, GMP & Subscriptions | Alambrix IPO';

const TOPICS = ['Listings', 'Allotments', 'GMP Estimates', 'Subscriptions', 'Anchor Book Filings', 'In-depth Analysis'];

const DATE_LABEL = new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' });

export const IpoNewsPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = newsTabByKey(searchParams.get('tab')).key;
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('latest');
  const [includeUnrelated, setIncludeUnrelated] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [notice, setNotice] = useState(null);

  // GET /api/market/news (shared with the landing page's news section)
  const articles = useSelector(selectNewsArticles);
  const { status, error, refreshedAt, source } = useSelector((state) => state.news);
  const gmpRows = useSelector(selectGmpBoardRows);
  const subscriptionRows = useSelector(selectSubscriptionRows);

  useEffect(() => {
    dispatch(fetchNews({ maxAgeMs: NEWS_MAX_AGE_MS }));
    dispatch(fetchGmp({ maxAgeMs: DATA_MAX_AGE_MS }));
    dispatch(fetchSubscriptionSummary({ maxAgeMs: DATA_MAX_AGE_MS }));
  }, [dispatch]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [tab, query, sort, includeUnrelated]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const rows = useMemo(() => buildNewsDeskRows(articles), [articles]);
  const tabCounts = useMemo(() => countNewsTabs(rows, { includeUnrelated }), [rows, includeUnrelated]);
  const stories = useMemo(() => filterNewsDesk(rows, { tab, query, sort, includeUnrelated }), [rows, tab, query, sort, includeUnrelated]);
  const unrelatedCount = rows.filter((row) => !row.ipoRelated).length;

  const hasRows = rows.length > 0;
  const isLoading = !hasRows && (status === 'idle' || status === 'loading');
  const refreshedLabel = formatRefreshedAt(refreshedAt);
  const dateLabel = refreshedAt ? DATE_LABEL.format(new Date(refreshedAt)) : null;

  const changeTab = (key) => setSearchParams(key === 'all' ? {} : { tab: key }, { replace: true });
  const clearFilters = () => {
    changeTab('all');
    setQuery('');
  };

  let emptyMessage;
  if (!hasRows && status === 'failed') {
    emptyMessage = (
      <>
        IPO news is temporarily unavailable{error ? ` (${error})` : ''}.{' '}
        <button type="button" onClick={() => dispatch(fetchNews())} className="font-bold text-[#008080] hover:underline">
          Retry
        </button>
      </>
    );
  } else if (!hasRows) {
    emptyMessage = 'No IPO news right now. New stories appear here as soon as they are published.';
  } else if (tab !== 'all' || query) {
    emptyMessage = (
      <>
        No {tab !== 'all' ? `"${newsTabByKey(tab).label}"` : ''} stories match right now.{' '}
        <button type="button" onClick={clearFilters} className="font-bold text-[#008080] hover:underline">
          Show all news
        </button>
      </>
    );
  } else {
    emptyMessage = 'No IPO-related stories in the latest feed.';
  }

  const shownCount = Math.min(visibleCount, stories.length);
  const headerLabel = `Showing ${tab === 'all' && !query ? 'latest ' : ''}${shownCount} ${shownCount === 1 ? 'update' : 'updates'}${
    dateLabel ? ` (${dateLabel})` : ''
  }`;

  let footerNote = null;
  if (unrelatedCount > 0) {
    footerNote = includeUnrelated ? (
      <button type="button" onClick={() => setIncludeUnrelated(false)} className="font-semibold text-[#008080] hover:underline">
        Hide general market stories
      </button>
    ) : (
      <>
        {unrelatedCount} general market {unrelatedCount === 1 ? 'story' : 'stories'} without an IPO angle{' '}
        {unrelatedCount === 1 ? 'is' : 'are'} hidden.{' '}
        <button type="button" onClick={() => setIncludeUnrelated(true)} className="font-semibold text-[#008080] hover:underline">
          Show {unrelatedCount === 1 ? 'it' : 'them'}
        </button>
      </>
    );
  }

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-5 sm:pt-6 pb-12 sm:pb-16 space-y-5 sm:space-y-6">
          <div>
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
              <Link to="/" className="hover:text-[#008080] transition-colors">
                Home
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <Link to="/ipo-dashboard" className="hover:text-[#008080] transition-colors">
                IPO Intelligence
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-slate-900 font-semibold" aria-current="page">
                Real-Time News Desk
              </span>
            </nav>

            <div className="mt-3 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">IPO News</h1>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#008080] text-[11px] font-extrabold uppercase tracking-wide text-white">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" aria-hidden="true" />
                    Live Desk
                  </span>
                </div>
                <p className="mt-2 text-xs sm:text-sm font-semibold text-slate-500">{TOPICS.join(' • ')}</p>
                {refreshedLabel && (
                  <p className="mt-1 text-[11px] text-slate-400">
                    Updated {refreshedLabel}
                    {source ? ` · via ${source}` : ''}
                  </p>
                )}
              </div>
              <label className="relative block w-full lg:w-80">
                <span className="sr-only">Search IPO news</span>
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search within primary news..."
                  className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 shadow-sm focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]"
                />
              </label>
            </div>
          </div>

          {/* Lifecycle tabs + sort */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div role="group" aria-label="News category" className="flex gap-1.5 overflow-x-auto pb-1 lg:pb-0 scrollbar-none">
              {NEWS_DESK_TABS.map((option) => {
                const active = option.key === tab;
                return (
                  <button
                    key={option.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => changeTab(option.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border transition-colors ${
                      active ? 'bg-[#008080] border-[#008080] text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900'
                    }`}
                  >
                    {option.label}
                    <span className={`px-1.5 py-px rounded-full text-[10px] ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>
                      {tabCounts[option.key] || 0}
                    </span>
                  </button>
                );
              })}
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-500 shrink-0">
              Sort:
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]"
              >
                {NEWS_SORTS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* Sponsored banner (GET /api/ads): dedicated news slot first, then the home leaderboard */}
          <AdSlot slotId={['ipo-news-top', 'leaderboard']} />

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_320px] gap-5 sm:gap-6 items-start">
            <NewsDeskList
              stories={stories}
              visibleCount={visibleCount}
              onLoadMore={() => setVisibleCount((count) => count + PAGE_SIZE)}
              headerLabel={headerLabel}
              syncLabel={hasRows ? 'Stream synchronised' : null}
              isLoading={isLoading}
              isRefreshing={status === 'loading'}
              onRefresh={() => dispatch(fetchNews())}
              emptyMessage={emptyMessage}
              footerNote={footerNote}
            />
            <NewsDeskSidebar
              gmpRows={gmpRows}
              subscriptionRows={subscriptionRows}
              onAlertSignup={() => setNotice("Daily IPO alerts are coming soon — your email wasn't saved.")}
            />
          </div>
        </div>

        {notice && (
          <div
            role="status"
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md flex items-start gap-2 rounded-xl bg-slate-900 text-white text-xs px-4 py-3 shadow-elevated"
          >
            <Info className="w-4 h-4 flex-shrink-0 text-teal-300" />
            <span className="flex-1">{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </>
  );
};

export default IpoNewsPage;
